import type { AxiosResponse } from 'axios';
import type {
  AllocationFailureMarker,
  AllocationSuccessfulMarker
} from 'pages/marking/subpages/marking-allocation-tab/types';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import type { MarkStatus } from 'services/assist-api/entities/test-instance-response-mark/types';
import type { BaseMarkingTask } from 'services/assist-api/entities/test-instance-response-marker/marking-tasks/types';
import type { TestInstanceResponseMarkerWithUser } from 'services/assist-api/entities/test-instance-response-marker/types';

/**
 * Filters a list of marking tasks, returning only the tasks where the organisation user is a marker.
 */
export function getOrganisationUserMarkingTasks(
  organisationUserId: number,
  markingTasks: BaseMarkingTask[]
): BaseMarkingTask[] {
  return markingTasks.filter(
    (mt) => mt.testInstanceResponseMarkerCustomersUsersId === organisationUserId
  );
}

/**
 * Returns true if the responses allocated to the marker matches the number where they are a marker.
 */
export function isAlreadyAllocatedToAllResponses(
  organisationUserMarkingTasks: BaseMarkingTask[],
  selectedResponseIds: number[]
): boolean {
  const numberOfResponsesAllocatedToUser = selectedResponseIds.filter((responseId) =>
    organisationUserMarkingTasks.find((mt) => mt.testInstanceResponseId === responseId)
  ).length;
  return numberOfResponsesAllocatedToUser === selectedResponseIds.length;
}

/**
 * Returns the number of marking tasks that have the specified statuses.
 */
export function markingTasksByStatusCount(
  organisationUserMarkingTasks: BaseMarkingTask[],
  markStatuses: (MarkStatus | null)[]
): number {
  return organisationUserMarkingTasks.filter((markingTask) =>
    markStatuses.includes(markingTask.testInstanceResponseMarkStatus)
  ).length;
}

export function successfullyAllocatedMarkers(
  organisationUsers: BaseCustomerUser[],
  fulfilledResponses: PromiseFulfilledResult<
    AxiosResponse<RequestResponse<TestInstanceResponseMarkerWithUser>>
  >[]
): AllocationSuccessfulMarker[] {
  return fulfilledResponses.map((fulfilledResponse) => {
    const organisationUser = organisationUsers.find(
      (ou) => ou.id === fulfilledResponse.value.data.json.user.id
    );
    return {
      responseId: fulfilledResponse.value.data.json.testInstanceResponse.id,
      organisationUserId: organisationUser?.id || 0,
      username: organisationUser?.user.username || '',
      name: `${organisationUser?.user.firstname} ${organisationUser?.user.lastname}`
    };
  });
}

export function unsuccessfullyAllocatedMarkers(
  organisationUsers: BaseCustomerUser[],
  rejectedResponses: PromiseRejectedResult[]
): AllocationFailureMarker[] {
  return rejectedResponses.map((rejectedResponse) => {
    /* Note: the only part of the response that has the original request IDs is the data property
       which contains the original request data. We need to parse this JSON and extract the test
       instance response ID and the organisation user ID. */
    const rejectedRequestData = JSON.parse(rejectedResponse.reason.config.data);

    const organisationUserEntityIdParts =
      rejectedRequestData.data.relationships.user.data.id.split('/');
    const organisationUserId =
      organisationUserEntityIdParts[organisationUserEntityIdParts.length - 1];
    const organisationUser = organisationUsers.find((ou) => ou.id === +organisationUserId);

    const testInstanceResponseEntityIdParts =
      rejectedRequestData.data.relationships.testInstanceResponse.data.id.split('/');
    const testInstanceResponseId =
      testInstanceResponseEntityIdParts[testInstanceResponseEntityIdParts.length - 1];

    return {
      responseId: testInstanceResponseId,
      organisationUserId: organisationUser?.id || 0,
      username: organisationUser?.user.username || '',
      name: `${organisationUser?.user.firstname} ${organisationUser?.user.lastname}`,
      failureReason: rejectedResponse?.reason?.response?.data?.detail
    };
  });
}
